import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Platform } from '@/platform';

/**
 * Touch and sound feedback for the studio.
 *
 * What this is for: making an action feel like it landed. A colour poured, a
 * tool was chosen, a picture was finished. Nothing here rewards or congratulates
 * — there is no streak chime and no "well done" fanfare, because a child should
 * be drawing for the drawing (spec §7 C04, §4.1).
 *
 * Three rules:
 *
 *  1. Never claim a capability that isn't there. Haptics are real on the iPad
 *     and iPhone build via the native plugin; on the web, `navigator.vibrate`
 *     exists on some Android browsers and nowhere on iOS Safari. A missing
 *     one is silence, not an error and not a fallback that pretends.
 *
 *  2. Audio is SYNTHESISED, not sampled. A handful of oscillators means no
 *     asset downloads, nothing to fail offline, and a few hundred bytes
 *     instead of a few hundred kilobytes in an app that has to work on a
 *     plane (spec §15).
 *
 *  3. Browsers refuse to start audio before a real gesture, so the context is
 *     created lazily inside the first tap and simply skipped if it is refused.
 */

type Voice = { freq: number; to?: number; dur: number; gain: number; type?: OscillatorType };

let enabled = true;
let ctx: AudioContext | null = null;
let failed = false;

function audio(): AudioContext | null {
  if (failed || !enabled) return null;
  if (ctx) {
    // Some browsers suspend the context when the tab loses focus.
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
    return ctx;
  }
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      failed = true;
      return null;
    }
    ctx = new Ctor();
    return ctx;
  } catch {
    failed = true;
    return null;
  }
}

function tone({ freq, to, dur, gain, type = 'sine' }: Voice) {
  const ac = audio();
  if (!ac) return;
  try {
    const osc = ac.createOscillator();
    const amp = ac.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ac.currentTime);
    if (to) osc.frequency.exponentialRampToValueAtTime(to, ac.currentTime + dur);
    // A quick attack and a soft tail: a click, not a beep.
    amp.gain.setValueAtTime(0.0001, ac.currentTime);
    amp.gain.exponentialRampToValueAtTime(gain, ac.currentTime + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);
    osc.connect(amp).connect(ac.destination);
    osc.start();
    osc.stop(ac.currentTime + dur + 0.02);
  } catch {
    /* audio is a nicety; never let it break drawing */
  }
}

async function buzz(style: ImpactStyle) {
  if (!enabled) return;
  if (Platform.isNative) {
    try {
      await Haptics.impact({ style });
      return;
    } catch {
      /* the device may have haptics disabled system-wide */
    }
  }
  // Android web. iOS Safari has no vibrate API at all, and pretending
  // otherwise would just be a no-op with extra steps.
  if (typeof navigator.vibrate === 'function') {
    navigator.vibrate(style === ImpactStyle.Heavy ? 18 : style === ImpactStyle.Medium ? 12 : 8);
  }
}

export const Feedback = {
  /** Bedtime and reduced-motion turn everything here off. */
  setEnabled(on: boolean) {
    enabled = on;
    if (!on && ctx && ctx.state === 'running') void ctx.suspend().catch(() => undefined);
  },

  get enabled() {
    return enabled;
  },

  /** Choosing a tool, a colour or a size. */
  pick() {
    tone({ freq: 880, to: 1320, dur: 0.06, gain: 0.05, type: 'triangle' });
    void buzz(ImpactStyle.Light);
  },

  /** A shape filling with colour — a short pour, low to high. */
  fill() {
    tone({ freq: 300, to: 720, dur: 0.16, gain: 0.06, type: 'sine' });
    void buzz(ImpactStyle.Medium);
  },

  /** Taking something back. Deliberately duller than doing something. */
  undo() {
    tone({ freq: 520, to: 300, dur: 0.1, gain: 0.045, type: 'sine' });
    void buzz(ImpactStyle.Light);
  },

  /** A picture finished. One rising pair, once — not a fanfare. */
  finish() {
    tone({ freq: 660, dur: 0.14, gain: 0.05, type: 'triangle' });
    window.setTimeout(() => tone({ freq: 990, dur: 0.2, gain: 0.045, type: 'triangle' }), 110);
    if (Platform.isNative) {
      void Haptics.notification({ type: NotificationType.Success }).catch(() => undefined);
    } else {
      void buzz(ImpactStyle.Medium);
    }
  },
};
